import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { getShootingMethodLabelAr, getWorkloadLabelAr, getShootingPlaceTypeLabelAr, getProductPhysicalSizeLabelAr } from '../../../utils/bookingLabels';
import { formatNumber } from '../../../utils/formatNumber';

const StarRating = ({ rating }) => {
  const num = parseFloat(rating) || 0;
  const filled = Math.round(num);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} className={`w-4 h-4 ${i <= filled ? 'text-amber-400' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const expBadge = {
  'مصور معتمد':   { bg: 'bg-green-100',  text: 'text-green-700' },
  'مصور مميز':    { bg: 'bg-amber-100',   text: 'text-amber-700' },
  'مصور نخبة':    { bg: 'bg-violet-100',  text: 'text-violet-700' },
};

const BookingDetailsCard = ({ bookingDetails, totalPrice, paymentStatus }) => {
  const req = bookingDetails?.sessionRequirements || {};

  const detailChips = [
    { icon: 'Calendar', label: 'التاريخ',      value: bookingDetails?.date },
    { icon: 'Clock',    label: 'الوقت',        value: bookingDetails?.time },
    { icon: 'Timer',    label: 'المدة',         value: bookingDetails?.duration },
    { icon: 'Camera',   label: 'نوع التصوير',  value: bookingDetails?.photographyType },
  ].filter(c => c.value);

  const methods = req.shootingMethods || [];
  const deliverableLabel = req.deliverableLabel || [
    req.photosCount > 0 ? `${req.photosCount} صورة` : null,
    req.videosCount > 0 ? `${req.videosCount} فيديو طويل` : null,
    req.shortVideosCount > 0 ? `${req.shortVideosCount} فيديو قصير` : null,
  ].filter(Boolean).join(' + ');

  const sessionTags = [
    methods.length > 0 && { label: methods.map(m => getShootingMethodLabelAr(m)).filter(Boolean).join('، ') },
    req.workloadBucket && { label: getWorkloadLabelAr(req.photographyType, req.workloadBucket) },
    req.shootingPlaceType && { label: getShootingPlaceTypeLabelAr(req.shootingPlaceType) },
    req.requestedExperienceLevel && { label: req.requestedExperienceLevel },
    req.photographyType === 'product' && req.productPhysicalSizeBucket && { label: getProductPhysicalSizeLabelAr(req.productPhysicalSizeBucket) },
    req.deliverableMode !== 'unspecified' && deliverableLabel && { label: deliverableLabel },
  ].filter(Boolean);

  const badge = expBadge[bookingDetails?.experienceLevel] || { bg: 'bg-slate-100', text: 'text-slate-600' };

  const isPaid = paymentStatus?.status === 'paid';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

      {/* Photographer */}
      <div className="p-5 flex items-center gap-4 border-b border-slate-100">
        <Image
          src={bookingDetails?.photographerImage}
          alt={bookingDetails?.photographerImageAlt}
          className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <h3 className="text-base font-bold text-slate-900 leading-tight">{bookingDetails?.photographerName}</h3>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge.bg} ${badge.text}`}>
              {bookingDetails?.experienceLevel}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <StarRating rating={bookingDetails?.photographerRating} />
            <span className="text-sm font-semibold text-slate-700">{bookingDetails?.photographerRating}</span>
            {parseInt(bookingDetails?.photographerReviews) > 0 && (
              <span className="text-xs text-slate-400">({bookingDetails?.photographerReviews} تقييم)</span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
            <Icon name="MapPin" size={11} />
            <span>{bookingDetails?.photographerCity}</span>
          </div>
        </div>
      </div>

      {/* Booking details grid */}
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-2.5">
          {detailChips.map((chip, i) => (
            <div key={i} className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <Icon name={chip.icon} size={14} className="text-primary flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-slate-400 leading-none mb-0.5">{chip.label}</p>
                <p className="text-xs font-semibold text-slate-800 leading-tight truncate">{chip.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Location */}
        {bookingDetails?.location && bookingDetails.location !== 'غير محدد' && (
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 flex items-start gap-2">
            <Icon name="MapPin" size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-slate-400 leading-none mb-0.5">الموقع</p>
              <p className="text-xs font-semibold text-slate-800 leading-tight">{bookingDetails.location}</p>
            </div>
            {bookingDetails?.locationDetails?.lat && bookingDetails?.locationDetails?.lng && (
              <a
                href={`https://www.google.com/maps?q=${bookingDetails.locationDetails.lat},${bookingDetails.locationDetails.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 text-primary text-xs font-medium underline"
              >
                خريطة
              </a>
            )}
          </div>
        )}

        {/* Session tags */}
        {sessionTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {sessionTags.map((tag, i) => (
              <span key={i} className="text-xs bg-primary/8 text-primary px-2.5 py-1 rounded-full font-medium">
                {tag.label}
              </span>
            ))}
          </div>
        )}

        {/* Notes */}
        {req.notes && (
          <div className="bg-slate-50 rounded-xl px-3 py-2.5">
            <p className="text-[10px] text-slate-400 mb-1">ملاحظاتك للمصور</p>
            <p className="text-xs text-slate-700 leading-relaxed">{req.notes}</p>
          </div>
        )}
      </div>

      {/* Price row */}
      <div className="mx-5 mb-5 bg-primary/5 border border-primary/15 rounded-xl px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Banknote" size={16} className="text-primary" />
          <span className="text-sm font-semibold text-slate-700">السعر الإجمالي</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-primary">{formatNumber(totalPrice)} ر.س</span>
          {isPaid && (
            <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">مدفوع</span>
          )}
        </div>
      </div>

    </div>
  );
};

export default BookingDetailsCard;
